/**
 * Merchant-first home page — iteration 36 (pure B2B refresh).
 * The main cyannect.com site is now brand/merchant-facing only. Zero shopper
 * content in the hero/social-proof surfaces. A tiny "not a merchant?" footer
 * link discreetly deflects any accidental shopper who lands here.
 */
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, ShieldCheck, LineChart as LineChartIcon, Zap, PhoneOutgoing, Store, BarChart3, Wallet } from "lucide-react";
import { urlOnHost } from "@/lib/host";
import LogoRibbon from "@/components/LogoRibbon";
import TestimonialsGrid from "@/components/TestimonialsGrid";
import Seo from "@/components/Seo";
import { DemoCta } from "@/components/demo/DemoCta";

const stats = [
  { label: "Verified merchant conversions / mo", value: "$12.4M" },
  { label: "Merchants live", value: "1,240" },
  { label: "Attribution accuracy", value: "99.4%" },
  { label: "Avg. ROAS lift", value: "3.8×" },
];

const pillars = [
  {
    icon: TrendingUp,
    title: "Give shoppers a reason to choose your offer",
    body: [
      "Cyannect helps merchants reach shoppers who are actively looking for savings — and rewards eligible purchases with 1.20% cashback.",
      "Shoppers discover your offer on Cyannect, purchase directly from your website, and earn rewards when the sale is verified.",
      "You keep the customer relationship. We help make the promotion measurable.",
      "Shopper rewards can be redeemed for gift cards once the minimum redemption threshold is reached.",
    ],
  },
  {
    icon: LineChartIcon,
    title: "Full-funnel attribution",
    body: "Click, coupon, session, employee, country, device — captured for every redemption. Export to CSV, pipe to your CDP, or plug into your Snowflake.",
  },
  {
    icon: ShieldCheck,
    title: "Fraud-resistant by design",
    body: "Per-code CYN- QR issuance, atomic wallet ledgers, sale-amount caps, and rate-limited APIs keep both your budget and shoppers safe.",
  },
  {
    icon: Zap,
    title: "Ship a promo in 90 seconds",
    body: "Draft a campaign, upload a logo, set a commission, publish. Our AI copy nudger even A/B tests headlines for you.",
  },
];

export default function MerchantHome() {
  const appUrl = urlOnHost("app", "/");
  return (
    <div className="relative overflow-hidden">
      <Seo
        title="Performance commerce for Canadian merchants"
        description="Cyannect is the pay-for-performance commerce layer for Canadian brands. Verified conversion attribution, full-funnel reporting, and CAD-priced Starter and Growth plans built for ecommerce teams."
        keywords="Canadian merchant marketing, performance commerce, verified conversions, coupon platform, attribution, ROAS lift"
      />
      {/* Ambient backdrop */}
      <div className="fixed inset-0 pointer-events-none z-0 opacity-70">
        <div className="blob blob-cyan blob-float w-[720px] h-[720px] -top-40 -right-40" />
        <div className="blob blob-mint blob-float w-[560px] h-[560px] top-1/3 -left-40" style={{ animationDelay: "3s" }} />
      </div>

      {/* Hero */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 pt-20 pb-24">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/60 backdrop-blur border border-cyan-200 text-[11px] uppercase tracking-widest text-cyan-800 font-semibold mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> For brands & merchants — Turn offers into sales. Pay only when they convert.
        </div>
        <h1 className="font-display font-medium leading-[1.05] tracking-[-0.035em]" data-testid="merchant-hero-title">
          <div className="text-4xl md:text-5xl lg:text-6xl text-slate-900">Turn every dollar of ad spend</div>
          <div className="text-4xl md:text-5xl lg:text-6xl pf-gradient-text gradient-shift mt-1 pb-2">into a measurable sale.</div>
        </h1>
        <div className="mt-8 text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed space-y-2" data-testid="merchant-hero-subtitle">
          <p>Cyannect puts your offers in front of shoppers browsing our offers and cashback marketplace.</p>
          <p>Shoppers check out directly on your website through your own payment gateway. Cyannect never touches the sale.</p>
          <p>You pay a Performance Fee only when we verify a sale.</p>
          <p>Launch your first offer in minutes.</p>
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/register" className="pf-btn-primary px-6 py-3 rounded-full flex items-center gap-2" data-testid="merchant-cta-signup">
            List your brand <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/pricing" className="pf-btn-secondary px-6 py-3 rounded-full flex items-center gap-2" data-testid="merchant-cta-pricing">
            See plans
          </Link>
          <DemoCta testid="merchant-cta-demo" icon className="pf-btn-ghost px-6 py-3 rounded-full inline-flex items-center gap-2" />
          <a href="mailto:sales@cyannect.com" className="pf-btn-ghost px-6 py-3 rounded-full flex items-center gap-2" data-testid="merchant-cta-sales">
            <PhoneOutgoing className="w-4 h-4" /> Talk to sales
          </a>
        </div>

        <div className="mt-8 max-w-2xl rounded-2xl bg-white/80 backdrop-blur border border-slate-200 p-5 md:p-6" data-testid="home-basic-pricing">
          <div className="text-xs font-semibold uppercase tracking-widest text-cyan-700">Start with no monthly fee</div>
          <div className="mt-2 font-display text-2xl text-slate-900" data-testid="home-basic-pricing-plan">Basic — CA$0/month</div>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed" data-testid="home-basic-pricing-fee">
            Pay a Performance Fee only when Cyannect verifies a sale: 7.75% of the eligible sale value or CA$3.79, whichever is higher.
          </p>
          <p className="mt-1.5 text-sm text-slate-500 leading-relaxed" data-testid="home-basic-pricing-checkout">
            Sales happen on your website, through your own checkout. Cyannect never handles the payment.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link to="/register" className="pf-btn-primary px-5 py-2.5 rounded-full text-sm flex items-center gap-2" data-testid="home-basic-pricing-start">
              Start Free <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/pricing" className="pf-btn-secondary px-5 py-2.5 rounded-full text-sm flex items-center gap-2" data-testid="home-basic-pricing-plans">
              See Plans
            </Link>
          </div>
          <p className="mt-3 text-xs text-slate-500" data-testid="home-basic-pricing-note">No long-term contract. Upgrade anytime. Prefer lower Performance Fees? Starter is CA$49.99/month and Pro is CA$79.99/month.</p>
        </div>


        {/* Stats bar */}
        {/* <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((s) => (
            <div key={s.label} className="rounded-2xl bg-white/70 backdrop-blur border border-slate-200 p-5">
              <div className="text-3xl font-display font-medium text-slate-900 tabular-nums">{s.value}</div>
              <div className="text-xs text-slate-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div> */}
      </section>

      {/* Merchant logo ribbon — social proof */}
      {/* <LogoRibbon /> */}

      {/* Pillars */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 py-20">
        <div className="text-[10px] uppercase tracking-widest text-cyan-700 font-semibold mb-3">Why Cyannect</div>
        <h2 className="font-display text-3xl md:text-4xl text-slate-900 leading-tight max-w-3xl">
          Everything you need to launch, measure, and scale performance-driven promotions — all in one place.
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
          {pillars.map(({ icon: Icon, title, body }, i) => (
            <div key={title} className="rounded-2xl bg-white/80 backdrop-blur border border-slate-200 p-6" data-testid={`merchant-pillar-${i}`}>
              <div className="w-10 h-10 rounded-xl bg-cyan-50 border border-cyan-200 flex items-center justify-center mb-3">
                <Icon className="w-5 h-5 text-cyan-700" />
              </div>
              <div className="font-display text-xl text-slate-900">{title}</div>
              {Array.isArray(body)
                ? <div className="text-sm text-slate-600 mt-1.5 leading-relaxed space-y-2">{body.map((p) => <p key={p}>{p}</p>)}</div>
                : <div className="text-sm text-slate-600 mt-1.5 leading-relaxed">{body}</div>}
            </div>
          ))}
        </div>
      </section>

      {/* Product surfaces */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { icon: Store, title: "Storefront", copy: "Branded page shoppers land on when they see your logo in the app." },
          { icon: BarChart3, title: "Analytics", copy: "Live GMV, funnel drop-off, cohort retention, top codes, employee leaderboard." },
          { icon: Wallet, title: "Payouts", copy: "Automated cashback + affiliate payouts. Weekly invoices, Stripe Connect out-of-the-box." },
        ].map(({ icon: Icon, title, copy }) => (
          <div key={title} className="rounded-2xl bg-slate-900 p-6 relative overflow-hidden">
            <Icon className="w-6 h-6 text-cyan-400 mb-3" />
            <div className="font-semibold text-lg text-slate-400">{title}</div>
            <div className="text-xs text-slate-400 mt-1.5 leading-relaxed">{copy}</div>
          </div>
        ))}
      </section>

      {/* Merchant testimonials */}
      {/* <TestimonialsGrid /> */}

      {/* Final CTA */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 py-24 text-center">
        <h2 className="font-display text-3xl md:text-5xl text-slate-900 leading-tight">
          Ready to launch your first
          <span className="pf-gradient-text gradient-shift"> performance campaign?</span>
        </h2>
        <p className="text-slate-600 mt-4 max-w-xl mx-auto">
          Start with no monthly fee on Basic: CA$0/month. Pay a Performance Fee only on verified sales: 7.75% of the eligible sale value or CA$3.79, whichever is higher. Draft and publish your first offer in as little as 90 seconds. No credit card required to explore.
        </p>
        <div className="mt-8 flex justify-center gap-3 flex-wrap">
          <Link to="/register" className="pf-btn-primary px-6 py-3 rounded-full flex items-center gap-2" data-testid="merchant-cta-final">
            Get started <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/why-cyannect" className="pf-btn-ghost px-6 py-3 rounded-full">
            How it works →
          </Link>
        </div>
        <div className="mt-10 text-xs text-slate-500" data-testid="shopper-footer-note">
          Not a merchant? <a href={appUrl} className="text-cyan-700 font-medium underline underline-offset-2 hover:text-cyan-800" data-testid="shopper-footer-link">Get the Cyannect shopper app →</a>
        </div>
      </section>
    </div>
  );
}
